import { FormProvider, useForm } from 'react-hook-form';
import { InputHookForm } from '../../Common/Form/input-with-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { metadataSchema } from './schema';
import { Text } from '@chakra-ui/react';
import { MenuHookForm } from '../../Common/Form/menu-with-hook-form';
import Button from '../../Button';
import { useState } from 'react';
import ArrowRightIcon from '../../../assets/arrow-right-white-icon.svg';
import { TextAreaHookForm } from '../../Common/Form/textarea-hook-form';
import { UPLOAD_FILE_SUBMISSIONS_DESC } from '../../../constants/twygs.constant';
import FormInputCounter from './InputCountCharacter/form-input-counter.component';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import {
  getContentType,
  handleFileNameExtension,
  MAX_FILE_SIZE_10
} from '../../../constants/image.constant';
import DropzoneTwygsComponent from '../../Common/DropzoneTwygsComponent';
import axios from 'axios';
import { postAssetUpload } from '../../../apis/article.api';
import { createResponse } from '../../../apis/response.api';
import CreateResponseModal from './create-response-modal.component';
import { FieldHookForm } from '../../Common/Form/hook-form/field-hook-form';
import { TooltipComponent } from '../../Tooltip';
import InformIcon from '../../../assets/inform-gray-icon.svg';
import ToastBtnMessage from '../../ToastBtnMessage';
import { DataLayer } from '../../../utils/data-layer';
import { useAtom } from 'jotai';
import { profileAtom } from '../../Settings/atom';

const EQUITY_PERCENTAGE_TOOLTIP =
  'Enter the percentage of equity you want to propose. This information will be shared with the writer for their review.';

const ResponseEnterMetadataComponent = ({ request }) => {
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState('');
  const [fileMedia, setFileMedia] = useState('');
  const toast = useToast();
  const [profile] = useAtom(profileAtom);

  const navigate = useNavigate();

  const methods = useForm({
    resolver: yupResolver(metadataSchema)
  });
  const contentTypeId = methods.watch('contentTypeId');

  const handleCreateResponse = async (formValue) => {
    try {
      // Track content submission event
      const selectedContentType = request?.contentTypes?.find(
        (ct) => ct.id === formValue.contentTypeId
      );
      const contentTypeString = selectedContentType?.name || 'unknown';
      const username = profile?.username;

      DataLayer.trackContentSubmission(contentTypeString, username);

      let filenameUpload = handleFileNameExtension(selectedFile?.name, selectedFile?.type);
      let contentCloudKey = [];

      const match = window?.location?.href?.match(/\/request\/([^\/]+)\//);
      const requestId = match ? match[1] : null;
      if (!requestId) throw new Error('Invalid request id');

      if (selectedFile && fileMedia) {
        setLoading(true);
        contentCloudKey = await postAssetUpload({
          fileName: [filenameUpload]
        });
        const url = contentCloudKey?.[0]?.url;
        await axios.put(url, selectedFile, {
          headers: {
            'content-disposition': 'inline',
            'Content-Type': getContentType(filenameUpload.toLowerCase()),
            'x-amz-acl': 'public-read'
          }
        });
      }

      await createResponse({
        contentTypeIds: [formValue.contentTypeId],
        note: formValue.note,
        equityPercentage: Number(formValue.equityPercentage),
        title: formValue.title,
        contentCloudKey: contentCloudKey[0]?.key,
        requestId
      }).then(() => {
        setModal('create-success');
      });
    } catch (error) {
      toast({
        render: () => (
          <ToastBtnMessage
            title={error?.response?.data?.message}
            isError={true}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
    } finally {
      setLoading(false);
    }
  };

  const validateFiles = (file) => {
    let messageError = '';
    const fileValidator = getSupportFileTypes();
    if (!fileValidator.allowedFileTypes.includes(file?.type)) {
      messageError = fileValidator.errorFileTypeMessage;
    } else if (file?.size > fileValidator.fileSize) {
      messageError = fileValidator.errorFileSizeMessage;
    }

    methods.setError('content', {
      type: 'validate',
      message: messageError
    });
    return messageError;
  };

  const handleUploadMedia = (file) => {
    setFileMedia('');
    const newFile = file[0];
    setError(validateFiles(newFile));
    if (newFile && !validateFiles(newFile)) {
      setSelectedFile(newFile);
      const fakeUploadedUrl = URL.createObjectURL(newFile); // Replace with real upload logic
      setFileMedia(fakeUploadedUrl);
    }
  };

  const handleBackToSubmissionScreen = () => {
    navigate('/article/manage?tab=submissions');
  };

  const customYupValidator = {
    handleFieldChange: (event, methods, fieldName) => {
      methods.clearErrors(fieldName);
      methods.setValue(fieldName, event.target.value);
      methods.trigger(fieldName);
    },
    handleFormSubmit: async (event) => {
      event.preventDefault();

      // Validate content type matches uploaded file type
      if (selectedFile) {
        const fileType = selectedFile.type.includes('image') ? 1 : 2; // 1 for image, 2 for video
        if (fileType !== contentTypeId) {
          toast({
            render: () => (
              <ToastBtnMessage
                title="Content type does not match the uploaded file type."
                isError={true}
                color="jet-black"
                isHiddenButton
              />
            ),
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top-right'
          });
          return;
        }
      }

      await Promise.all([
        methods.trigger('title'),
        methods.trigger('contentTypeId'),
        methods.trigger('equityPercentage'),
        methods.trigger('note'),
        new Promise(async (resolve) => {
          let filenameUpload = handleFileNameExtension(selectedFile?.name, selectedFile?.type);
          if (!filenameUpload) {
            await methods.trigger('content');
            setError('Content is required');
            resolve(false);
          }
          resolve(true);
        })
      ]).then((values) => {
        if (values.every((value) => value === true)) handleCreateResponse(methods.getValues());
      });
    }
  };

  const getSupportFileTypes = () => {
    const result = {};
    switch (contentTypeId) {
      case 1: {
        result.allowedFileTypes = [
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/gif',
          'image/webp'
        ];
        result.errorFileTypeMessage =
          'Please ensure your file type is PNG, JPG, JPEG, GIF and WEBP';
        result.description = UPLOAD_FILE_SUBMISSIONS_DESC.image;
        result.fileSize = MAX_FILE_SIZE_10;
        result.errorFileSizeMessage = 'Please ensure your file size is less than 10MB';
        break;
      }
      case 2: {
        result.allowedFileTypes = [
          'video/mp4',
          'video/webm',
          'video/x-msvideo',
          'audio/mpeg',
          'audio/ogg',
          'video/ogg'
        ];
        result.errorFileTypeMessage = 'Please ensure your file type is MP4, OGG and WEBM';
        result.description = UPLOAD_FILE_SUBMISSIONS_DESC.video;
        result.fileSize = MAX_FILE_SIZE_10;
        result.errorFileSizeMessage = 'Please ensure your file size is less than 10MB';
        break;
      }
      default: {
        result.allowedFileTypes = [
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/gif',
          'image/webp',
          'video/mp4',
          'video/webm',
          'video/x-msvideo',
          'audio/mpeg',
          'audio/ogg',
          'video/ogg'
        ];
        result.errorFileTypeMessage =
          'Please ensure your file type is PNG, JPG, JPEG, GIF, WEBP, WEBM, MP4 and OGG';
        result.description = UPLOAD_FILE_SUBMISSIONS_DESC.all;
        result.fileSize = MAX_FILE_SIZE_10;
        result.errorFileSizeMessage = 'Please ensure your file size is less than 10MB';
      }
    }

    return result;
  };

  return (
    <div className="">
      <div>
        <FormProvider {...methods}>
          <form onSubmit={customYupValidator.handleFormSubmit}>
            <div className="flex flex-col gap-4 w-full">
              <FormInputCounter
                name="title"
                placeholder="ex.: Image of a dog"
                label="Submission title"
                maxLength={100}
                component={InputHookForm}
              />
              <MenuHookForm
                name="contentTypeId"
                label="Content Type"
                isShowCheckBox={false}
                placeholder={request?.contentTypes
                  ?.map((contentType) => ` ${contentType.name}`)
                  .toString()}
                data={request?.contentTypes || []}
                defaultValue={
                  request?.contentTypes?.length === 1 ? request?.contentTypes[0] : undefined
                }
                isDisabled={request?.contentTypes?.length === 1}
                isShowTooltip={true}
                tooltipDes={
                  request?.contentTypes?.length === 1
                    ? 'The content type is pre-set because the writer only accepts submissions in this format.'
                    : 'Select the type of content you are submitting. The writer only accepts one type per submission: Images or Videos.'
                }
              />
              <FormInputCounter
                component={TextAreaHookForm}
                css={{
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  '&': {
                    height: 'fit-content'
                  }
                }}
                name="note"
                placeholder="Share any background information or context for your content..."
                label="Notes"
                maxLength={2000}
                onChange={(event) => {
                  customYupValidator.handleFieldChange(event, methods, 'note');
                }}
              />
              <div>
                <div className="mb-6">
                  <h1>Content</h1>
                  <p className="text-sm !text-02-dim-gray">
                    This file will be viewed by the writer.
                  </p>
                </div>
                <div>
                  <FieldHookForm name="content" label="">
                    <DropzoneTwygsComponent
                      acceptFileTypes={getSupportFileTypes().allowedFileTypes}
                      error={error}
                      minH={{ base: '200px', md: '360px' }}
                      title={
                        <p className="!text-02-dim-gray text-sm">
                          <span className="!text-growth-green-500 font-[900]">
                            Click to upload file
                          </span>{' '}
                          or drag and drop
                        </p>
                      }
                      notMutiple
                      validateFiles={handleUploadMedia}
                      desc={getSupportFileTypes().description.map((item, index) => (
                        <Text
                          key={index}
                          maxW={'267px'}
                          fontWeight="400"
                          color="dim-gray"
                          fontSize="xs"
                        >
                          {item}
                        </Text>
                      ))}
                      previewMedia={fileMedia}
                      setPreviewMedia={setFileMedia}
                      previewMediaType={selectedFile?.type?.includes('image') ? 'image' : 'video'}
                    />
                  </FieldHookForm>
                </div>
              </div>
              {/* Equitity Percentage */}
              <div>
                <div className="flex items-center mb-[12px]">
                  <p className="mr-1">Equity Percentage for Negotiation</p>
                  <TooltipComponent Icon={InformIcon} description={EQUITY_PERCENTAGE_TOOLTIP} />
                </div>
                <FormInputCounter
                  name="equityPercentage"
                  placeholder="ex: 2%, 5%..."
                  label=""
                  component={InputHookForm}
                  isShowRange={false}
                />
              </div>
            </div>
            <div className="bg-white flex flex-col gap-3 md:flex-row justify-end border-t-[1px] border-solid border-04-anti-flash-white fixed left-0 bottom-0 z-20 py-[15px] md:py-[20px] px-[15px] md:px-[40px] w-full">
              <Button variant="outline" onClick={handleBackToSubmissionScreen}>
                Discard
              </Button>
              <Button type="submit" isLoading={loading}>
                Submit
                <img src={ArrowRightIcon} alt="" className="ml-2" />
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
      {modal === 'create-success' ? (
        <CreateResponseModal
          isOpen
          title="Response submitted"
          onClose={handleBackToSubmissionScreen}
          onSubmit={handleBackToSubmissionScreen}
          titleBtn="View My Submissions"
          minW="439px"
        />
      ) : null}
    </div>
  );
};

export default ResponseEnterMetadataComponent;
